<template>
  <div class="modal-overlay" @click="$emit('close')" data-cy="route-edit-modal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2>Edit Route: {{ route.name || 'Unnamed Route' }}</h2>
        <button @click="$emit('close')" class="btn-close">×</button>
      </div>
      
      <div class="modal-body">
        <div class="edit-controls">
          <div class="route-overview">
            <div class="route-info">
              <span class="route-distance">{{ (route.total_distance / 1000).toFixed(1) }}km</span>
              <span class="route-duration">{{ route.total_duration_formatted || formatDuration(route.total_duration) }}</span>
              <span class="route-type">{{ getSolverTypeDisplay() }}</span>
            </div>
          </div>
          
          <div class="vehicle-selector" v-if="route.routes && route.routes.length > 1">
            <label for="vehicle-select">Select Vehicle to Edit:</label>
            <select id="vehicle-select" v-model="selectedVehicleIndex" @change="onVehicleChange">
              <option 
                v-for="(vehicleRoute, index) in route.routes" 
                :key="index" 
                :value="index"
              >
                {{ getVehicleDisplayName(vehicleRoute, index) }} - {{ (vehicleRoute.distance / 1000).toFixed(1) }}km
              </option>
            </select>
          </div>
        </div>
        
        <div class="edit-content">
          <div class="stops-editor" v-if="selectedVehicleRoute">
            <h3>Route Stops (Drag to Reorder)</h3>
            <div class="stops-info">
              <span>Distance: {{ (selectedVehicleRoute.distance / 1000).toFixed(1) }}km</span>
              <span>Duration: {{ selectedVehicleRoute.duration_formatted || formatDuration(selectedVehicleRoute.duration_minutes) }}</span>
              <span>Stops: {{ selectedVehicleRoute.stops ? selectedVehicleRoute.stops.length : 0 }}</span>
            </div>
            
            <div class="stops-list" ref="stopsList">
              <div 
                v-for="(stopIndex, idx) in editableStops" 
                :key="`stop-${stopIndex}-${idx}`"
                class="stop-item"
                :class="{ 
                  'is-depot': isDepotLocation(stopIndex),
                  'is-dragging': draggedIndex === idx,
                  'drag-over': dragOverIndex === idx
                }"
                :draggable="!isDepotLocation(stopIndex)"
                @dragstart="onDragStart(idx, $event)"
                @dragenter="onDragEnter(idx, $event)"
                @dragover="onDragOver($event)"
                @dragleave="onDragLeave($event)"
                @drop="onDrop(idx, $event)"
                @dragend="onDragEnd($event)"
              >
                <div class="stop-handle" v-if="!isDepotLocation(stopIndex)">
                  <svg width="16" height="16" viewBox="0 0 24 24">
                    <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" fill="currentColor"/>
                  </svg>
                </div>
                
                <div class="stop-info">
                  <div class="stop-header">
                    <span class="stop-name">
                      {{ getLocationName(stopIndex) }}
                    </span>
                    <span class="stop-index" v-if="!isDepotLocation(stopIndex)">#{{ stopIndex }}</span>
                    <span class="depot-badge" v-else>DEPOT</span>
                  </div>
                  
                  <div class="stop-details">
                    <div class="stop-timing" v-if="getStopReachTime(stopIndex)">
                      <span class="timing-label">Arrival:</span>
                      <span class="timing-value">{{ getStopReachTime(stopIndex) }}</span>
                    </div>
                    
                    <div class="stop-demand" v-if="!isDepotLocation(stopIndex)">
                      <span class="demand-label">Demand:</span>
                      <span class="demand-value">{{ getLocationDemand(stopIndex) }} units</span>
                    </div>
                    
                    <div class="stop-coords">
                      <span class="coords-label">Location:</span>
                      <span class="coords-value">{{ formatCoords(getLocationCoords(stopIndex)) }}</span>
                    </div>
                  </div>
                </div>
                
                <div class="stop-actions" v-if="!isDepotLocation(stopIndex)">
                  <button 
                    class="btn-remove" 
                    @click="removeStop(idx)"
                    title="Remove this stop"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div class="preview-section">
            <h3>Route Preview</h3>
            <div class="route-summary">
              <div class="summary-item">
                <span class="summary-label">Original Distance:</span>
                <span class="summary-value">{{ (originalDistance / 1000).toFixed(1) }}km</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">New Distance:</span>
                <span class="summary-value" :class="{ 'changed': hasChanges }">
                  {{ estimatedDistance ? (estimatedDistance / 1000).toFixed(1) + 'km' : 'Calculating...' }}
                </span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Change:</span>
                <span class="summary-change" :class="getChangeClass()">
                  {{ getDistanceChange() }}
                </span>
              </div>
            </div>
            
            <div class="route-path" v-if="editableStops.length > 0">
              <h4>Route Path:</h4>
              <div class="path-list">
                <span 
                  v-for="(stopIndex, idx) in editableStops" 
                  :key="`path-${stopIndex}-${idx}`"
                  class="path-stop"
                  :class="{ 'is-depot': isDepotLocation(stopIndex) }"
                >
                  {{ getLocationName(stopIndex) }}
                  <svg v-if="idx < editableStops.length - 1" width="16" height="16" viewBox="0 0 24 24" class="arrow-right">
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" fill="currentColor"/>
                  </svg>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer">
        <button class="btn-cancel" @click="$emit('close')" :disabled="isUpdating">
          Cancel
        </button>
        <button 
          class="btn-save" 
          @click="saveChanges" 
          :disabled="!hasChanges || isUpdating"
          :class="{ loading: isUpdating }"
        >
          <span v-if="isUpdating" class="loading-spinner"></span>
          {{ isUpdating ? 'Updating Route...' : 'Save Changes' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:5000'

export default {
  name: 'RouteEditModal',
  props: {
    route: {
      type: Object,
      required: true
    },
    vehicles: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'updated'],
  data() {
    return {
      selectedVehicleIndex: 0,
      editableStops: [],
      originalStops: [],
      originalDistance: 0,
      estimatedDistance: null,
      isUpdating: false,
      draggedIndex: null,
      dragOverIndex: null,
      calculateDistanceTimeout: null
    }
  },
  computed: {
    selectedVehicleRoute() {
      if (!this.route.routes || this.selectedVehicleIndex >= this.route.routes.length) {
        return null
      }
      return this.route.routes[this.selectedVehicleIndex]
    },
    hasChanges() {
      return JSON.stringify(this.editableStops) !== JSON.stringify(this.originalStops)
    }
  },
  mounted() {
    this.initializeEditableStops()
  },
  methods: {
    initializeEditableStops() {
      if (this.selectedVehicleRoute && this.selectedVehicleRoute.stops) {
        this.editableStops = [...this.selectedVehicleRoute.stops]
        this.originalStops = [...this.selectedVehicleRoute.stops]
        this.originalDistance = this.selectedVehicleRoute.distance || 0
        this.estimatedDistance = this.originalDistance
      }
    },
    
    onVehicleChange() {
      this.initializeEditableStops()
    },
    
    getVehicleDisplayName(vehicleRoute, index) {
      if (this.getSolverType() === 'TSP' && this.route.routes.length === 1) {
        return 'Optimal Route'
      }
      
      if (vehicleRoute.vehicleId && this.vehicles.length > 0) {
        const vehicle = this.vehicles.find(v => v.id === vehicleRoute.vehicleId)
        return vehicle ? vehicle.plate : `Vehicle ${index + 1}`
      }
      
      return `Vehicle ${index + 1}`
    },
    
    getSolverType() {
      if (this.route.solver_type) {
        return this.route.solver_type.toUpperCase()
      }
      
      if ((this.route.demands && this.route.demands.some(d => d > 0)) || 
          this.route.vehicle_capacity || 
          this.route.vehicle_capacities) {
        return 'VRP'
      }
      
      if (this.route.routes && this.route.routes.length > 1) {
        return 'VRP'
      }
      
      return 'TSP'
    },
    
    getSolverTypeDisplay() {
      const solverType = this.getSolverType()
      switch (solverType) {
        case 'VRP': return 'Vehicle Routing Problem'
        case 'TSP': return 'Traveling Salesman Problem'
        case 'LAP': return 'Location Allocation Problem'
        default: return 'Unknown'
      }
    },
    
    isDepotLocation(locationIndex) {
      if (this.route.multiDepot && this.route.depots) {
        return this.route.depots.includes(locationIndex)
      }
      return locationIndex === 0
    },
    
    getLocationName(locationIndex) {
      if (this.route.locationNames && this.route.locationNames[locationIndex]) {
        return this.route.locationNames[locationIndex]
      }
      if (this.isDepotLocation(locationIndex)) {
        return 'Depot'
      }
      return `Location ${locationIndex}`
    },
    
    getLocationCoords(locationIndex) {
      if (this.route.allLocations && this.route.allLocations[locationIndex]) {
        return this.route.allLocations[locationIndex]
      }
      return null
    },
    
    getLocationDemand(locationIndex) {
      if (this.isDepotLocation(locationIndex)) {
        return 0
      }
      
      // Check route-level demands array
      if (this.route.demands && this.route.demands[locationIndex] !== undefined) {
        return this.route.demands[locationIndex]
      }
      
      // Check reach_times in current vehicle route
      if (this.selectedVehicleRoute && this.selectedVehicleRoute.reach_times) {
        const reachTime = this.selectedVehicleRoute.reach_times.find(rt => rt.location_index === locationIndex)
        if (reachTime && reachTime.demand !== undefined) {
          return reachTime.demand
        }
      }
      
      return 1 // Default
    },
    
    getStopReachTime(locationIndex) {
      if (!this.selectedVehicleRoute || !this.selectedVehicleRoute.reach_times) {
        return null
      }
      
      const reachTime = this.selectedVehicleRoute.reach_times.find(rt => rt.location_index === locationIndex)
      if (reachTime) {
        return reachTime.actual_arrival_time || reachTime.reach_time_formatted || this.formatRealTime(reachTime.reach_time_minutes)
      }
      
      return null
    },
    
    formatCoords(coords) {
      if (!coords || coords.length !== 2) return 'N/A'
      return `${coords[1].toFixed(5)}, ${coords[0].toFixed(5)}`
    },
    
    formatDuration(minutes) {
      if (!minutes && minutes !== 0) return 'N/A'
      const hours = Math.floor(minutes / 60)
      const mins = Math.floor(minutes % 60)
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
    },
    
    formatRealTime(minutes) {
      if (!minutes && minutes !== 0) return 'N/A'
      
      const startHour = 8 // Default start time 8:00 AM
      const startMinute = 0
      
      const totalStartMinutes = startHour * 60 + startMinute
      const totalMinutes = totalStartMinutes + minutes
      
      const hours = Math.floor(totalMinutes / 60) % 24
      const mins = Math.floor(totalMinutes % 60)
      
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
    },
    
    // Drag and drop functionality
    onDragStart(index, event) {
      this.draggedIndex = index
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/html', event.target.outerHTML)
    },
    
    onDragEnter(index, event) {
      event.preventDefault()
      if (this.draggedIndex !== null && this.draggedIndex !== index) {
        this.dragOverIndex = index
      }
    },
    
    onDragOver(event) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
    },
    
    onDragLeave(event) {
      // Only clear dragOverIndex if we're leaving the drop zone entirely
      // eslint-disable-next-line no-unused-vars
      if (!event.currentTarget.contains(event.relatedTarget)) {
        this.dragOverIndex = null
      }
    },
    
    onDrop(index, event) {
      event.preventDefault()
      
      if (this.draggedIndex === null || this.draggedIndex === index) {
        this.dragOverIndex = null
        return
      }
      
      // Don't allow dropping on depot locations
      const targetLocationIndex = this.editableStops[index]
      if (this.isDepotLocation(targetLocationIndex)) {
        this.dragOverIndex = null
        return
      }
      
      // Perform the reorder
      const draggedItem = this.editableStops[this.draggedIndex]
      
      // Remove the dragged item
      this.editableStops.splice(this.draggedIndex, 1)
      
      // Insert at new position (adjust index if we removed an item before the drop position)
      const insertIndex = this.draggedIndex < index ? index - 1 : index
      this.editableStops.splice(insertIndex, 0, draggedItem)
      
      this.draggedIndex = null
      this.dragOverIndex = null
      
      // Recalculate estimated distance
      this.debounceDistanceCalculation()
    },
    
    onDragEnd() {
      this.draggedIndex = null
      this.dragOverIndex = null
    },
    
    removeStop(index) {
      const locationIndex = this.editableStops[index]
      
      // Don't allow removing depot locations
      if (this.isDepotLocation(locationIndex)) {
        return
      }
      
      this.editableStops.splice(index, 1)
      this.debounceDistanceCalculation()
    },
    
    debounceDistanceCalculation() {
      if (this.calculateDistanceTimeout) {
        clearTimeout(this.calculateDistanceTimeout)
      }
      
      this.calculateDistanceTimeout = setTimeout(() => {
        this.estimateDistance()
      }, 500)
    },
    
    async estimateDistance() {
      if (!this.editableStops.length || !this.route.allLocations) {
        this.estimatedDistance = 0
        return
      }
      
      try {
        // Simple estimation using Haversine distance
        let totalDistance = 0
        
        for (let i = 0; i < this.editableStops.length - 1; i++) {
          const currentStop = this.editableStops[i]
          const nextStop = this.editableStops[i + 1]
          
          const currentCoords = this.route.allLocations[currentStop]
          const nextCoords = this.route.allLocations[nextStop]
          
          if (currentCoords && nextCoords) {
            const distance = this.calculateHaversineDistance(currentCoords, nextCoords)
            totalDistance += distance
          }
        }
        
        this.estimatedDistance = totalDistance
      } catch (error) {
        console.error('Error estimating distance:', error)
        this.estimatedDistance = null
      }
    },
    
    calculateHaversineDistance(coord1, coord2) {
      const R = 6371000 // Earth's radius in meters
      const lat1 = coord1[1] * Math.PI / 180
      const lat2 = coord2[1] * Math.PI / 180
      const deltaLat = (coord2[1] - coord1[1]) * Math.PI / 180
      const deltaLng = (coord2[0] - coord1[0]) * Math.PI / 180
      
      const a = Math.sin(deltaLat/2) * Math.sin(deltaLat/2) +
                Math.cos(lat1) * Math.cos(lat2) *
                Math.sin(deltaLng/2) * Math.sin(deltaLng/2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
      
      return R * c
    },
    
    getDistanceChange() {
      if (!this.estimatedDistance || this.estimatedDistance === this.originalDistance) {
        return '0.0km'
      }
      
      const change = this.estimatedDistance - this.originalDistance
      const sign = change > 0 ? '+' : ''
      return `${sign}${(change / 1000).toFixed(1)}km`
    },
    
    getChangeClass() {
      if (!this.estimatedDistance || this.estimatedDistance === this.originalDistance) {
        return 'no-change'
      }
      
      return this.estimatedDistance > this.originalDistance ? 'increase' : 'decrease'
    },
    
    async saveChanges() {
      if (!this.hasChanges || this.isUpdating) {
        return
      }
      
      this.isUpdating = true
      
      try {
        const routeId = this.route._id || this.route.id
        
        const response = await axios.patch(`${API_URL}/api/analytics/saved-routes/${routeId}`, {
          vehicleIndex: this.selectedVehicleIndex,
          newStopOrder: this.editableStops
        })
        
        if (response.data.success) {
          this.$emit('updated', response.data.route)
          this.$emit('close')
        } else {
          throw new Error(response.data.error || 'Failed to update route')
        }
        
      } catch (error) {
        console.error('Error saving route changes:', error)
        alert(`Failed to save changes: ${error.response?.data?.error || error.message}`)
      } finally {
        this.isUpdating = false
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 95vw;
  height: 90vh;
  max-width: 1000px;
  max-height: 700px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #eee;
  flex-shrink: 0;
}

.modal-header h2 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.4rem;
  font-weight: 600;
}

.btn-close {
  background: none;
  border: none;
  font-size: 2rem;
  color: #7f8c8d;
  cursor: pointer;
  padding: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-close:hover {
  background-color: #f8f9fa;
  color: #2c3e50;
}

.modal-body {
  flex: 1;
  padding: 1.5rem 2rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.edit-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
}

.route-overview .route-info {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.route-distance {
  font-size: 1.2rem;
  font-weight: bold;
  color: #3498db;
}

.route-duration {
  font-size: 1rem;
  color: #7f8c8d;
}

.route-type {
  background: #e8f4fd;
  color: #3498db;
  padding: 0.25rem 0.75rem;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
}

.vehicle-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.vehicle-selector label {
  font-weight: 600;
  color: #2c3e50;
}

.vehicle-selector select {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  min-width: 200px;
}

.edit-content {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 2rem;
  overflow: hidden;
}

.stops-editor {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.stops-editor h3 {
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.stops-info {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  color: #7f8c8d;
}

.stops-list {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 0.5rem;
}

.stop-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  background: white;
  border: 1px solid #eee;
  border-radius: 8px;
  transition: all 0.2s;
  cursor: move;
}

.stop-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.stop-item.is-depot {
  background: #fdf2e9;
  border-color: #f39c12;
  cursor: default;
}

.stop-item.is-dragging {
  opacity: 0.5;
  transform: rotate(2deg);
}

.stop-item.drag-over {
  border-color: #3498db;
  background: #e8f4fd;
}

.stop-handle {
  color: #7f8c8d;
  cursor: grab;
  padding: 0.25rem;
}

.stop-handle:active {
  cursor: grabbing;
}

.stop-item.is-depot .stop-handle {
  opacity: 0.3;
  cursor: not-allowed;
}

.stop-info {
  flex: 1;
}

.stop-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.stop-name {
  font-weight: 600;
  color: #2c3e50;
}

.stop-index {
  background: #ecf0f1;
  color: #7f8c8d;
  padding: 0.1rem 0.4rem;
  border-radius: 12px;
  font-size: 0.75rem;
}

.depot-badge {
  background: #f39c12;
  color: white;
  padding: 0.1rem 0.4rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.stop-details {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: 0.8rem;
  color: #7f8c8d;
}

.timing-value, .demand-value, .coords-value {
  color: #2c3e50;
  font-weight: 500;
}

.stop-actions {
  display: flex;
  align-items: center;
}

.btn-remove {
  background: none;
  border: none;
  color: #e74c3c;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-remove:hover {
  background: #fdf2f2;
  color: #c0392b;
}

.preview-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.preview-section h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.route-summary {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.summary-item:last-child {
  margin-bottom: 0;
}

.summary-label {
  color: #7f8c8d;
  font-size: 0.9rem;
}

.summary-value {
  font-weight: 600;
  color: #2c3e50;
}

.summary-value.changed {
  color: #3498db;
}

.summary-change {
  font-weight: 600;
}

.summary-change.increase {
  color: #e74c3c;
}

.summary-change.decrease {
  color: #27ae60;
}

.summary-change.no-change {
  color: #7f8c8d;
}

.route-path {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
}

.route-path h4 {
  margin: 0 0 0.75rem 0;
  color: #2c3e50;
  font-size: 1rem;
}

.path-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
}

.path-stop {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 16px;
  padding: 0.25rem 0.75rem;
  font-size: 0.8rem;
  color: #2c3e50;
}

.path-stop.is-depot {
  background: #fdf2e9;
  border-color: #f39c12;
  color: #f39c12;
  font-weight: 600;
}

.arrow-right {
  color: #7f8c8d;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid #eee;
  flex-shrink: 0;
}

.btn-cancel, .btn-save {
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-cancel {
  background: #ecf0f1;
  color: #7f8c8d;
  border: 1px solid #bdc3c7;
}

.btn-cancel:hover:not(:disabled) {
  background: #d5dbdb;
  color: #2c3e50;
}

.btn-save {
  background: #3498db;
  color: white;
  border: 1px solid #2980b9;
}

.btn-save:hover:not(:disabled) {
  background: #2980b9;
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.3);
}

.btn-save:disabled {
  background: #bdc3c7;
  color: #7f8c8d;
  cursor: not-allowed;
  border-color: #95a5a6;
}

.btn-save.loading {
  background: #95a5a6;
}

.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive design */
@media (max-width: 768px) {
  .modal-content {
    width: 100vw;
    height: 100vh;
    border-radius: 0;
    max-width: none;
    max-height: none;
  }
  
  .edit-content {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;
  }
  
  .edit-controls {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .stop-details {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>